import { useSyncExternalStore } from 'react';
import axios from 'axios';
import config from '../config';

/*
  Préférence « Économie de données » de l'utilisateur connecté.

  POURQUOI UN HOOK DÉDIÉ plutôt que d'étendre usePermissions :
  usePermissions garde son résultat dans un cache AU NIVEAU MODULE qui n'est
  JAMAIS invalidé — il ne se vide qu'au rechargement de la page. Sur l'APK
  (WebView Capacitor), l'utilisateur ne peut pas « recharger » : la bascule
  Éco resterait sans effet jusqu'au prochain démarrage de l'app. Ici, le cache
  est notifiable : setDataSaver() met à jour et prévient tous les abonnés, donc
  l'écran change immédiatement.

  La valeur arrive avec /me (GET /api/permissions/me → { role, permissions, prefs })
  pour ne pas ajouter d'aller-retour réseau au démarrage.
*/

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const DEFAUT = 'auto';

let valeur = DEFAUT;
let charge = false;
let enVol = null;
const abonnes = new Set();

const notifier = () => abonnes.forEach((f) => f());
const abonner = (f) => { abonnes.add(f); return () => abonnes.delete(f); };
const lire = () => valeur;

/** Charge la préférence depuis /me (une seule fois, requêtes concurrentes fusionnées). */
// Horloge logique : toute écriture locale (setDataSaver) incrémente ce compteur.
// Une réponse de /me partie AVANT une écriture réussie est donc reconnaissable
// et ignorée — sinon, sur une connexion lente, le chargement initial écrasait la
// préférence que l'utilisateur venait d'enregistrer avec succès (elle semblait
// « ne pas tenir »).
let version = 0;

const charger = () => {
  if (charge || enVol) return enVol;
  const versionAuDepart = version;
  enVol = axios.get(`${DOMAIN}/api/permissions/me`)
    .then(({ data }) => {
      if (version !== versionAuDepart) return;   // écrasé entre-temps : on jette
      const v = data && data.prefs && data.prefs.data_saver;
      valeur = (v === 'eco' || v === 'qualite' || v === 'auto') ? v : DEFAUT;
    })
    // Silencieux : une préférence illisible ne doit jamais casser un écran.
    // 'auto' reste un défaut sûr (Éco sur mobile, Qualité sur PC).
    .catch(() => { if (version === versionAuDepart) valeur = DEFAUT; })
    .finally(() => { charge = true; enVol = null; notifier(); });
  return enVol;
};

/** Met à jour la valeur locale et prévient les abonnés (appeler après le PUT). */
export const setDataSaver = (v) => {
  valeur = (v === 'eco' || v === 'qualite' || v === 'auto') ? v : DEFAUT;
  charge = true;
  version += 1;
  notifier();
};

/** Vide le cache : à appeler à la déconnexion, sinon l'utilisateur suivant sur
 *  le même appareil (APK partagée) hériterait de la préférence du précédent. */
export const resetDataSaver = () => {
  valeur = DEFAUT;
  charge = false;
  enVol = null;
  version += 1;
  notifier();
};

/** Enregistre côté serveur ET localement. */
export const enregistrerDataSaver = async (v) => {
  const { data } = await axios.put(`${DOMAIN}/api/user/display-pref`, { data_saver: v });
  setDataSaver(data && data.data_saver);
  return data;
};

/** @returns {'auto'|'eco'|'qualite'} */
export default function useDataSaver() {
  const v = useSyncExternalStore(abonner, lire, lire);
  if (!charge) charger();
  return v;
}
